import React, { useRef } from 'react';
import { Card, Row, Col, Button, Alert } from 'react-bootstrap';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';
import html2pdf from 'html2pdf.js';
import './ScanResults.css';

type MetricValue = number | string | null | undefined;
type MetricBadge = 'LOW' | 'NORMAL' | 'HIGH';
type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'skin' | 'neutral';
type MetricCategory =
  | 'Vital Signs'
  | 'Heart & Cardiovascular'
  | 'Metabolic'
  | 'Fitness & Body Composition'
  | 'HRV / Recovery';
type SkinRoutineTitle = 'Morning Routine' | 'Evening Routine' | 'Weekly Treatments' | 'Lifestyle Tips';

interface UserProfile {
  name?: string;
  age?: MetricValue;
  gender?: string;
  height?: MetricValue;
  weight?: MetricValue;
}

interface MetricSource {
  [key: string]: MetricValue;
}

interface ScanMetadata {
  device?: string;
  fps?: number;
  physiological_scores?: MetricSource & UserProfile;
  heartrate_time?: number[];
  resp_rate_time?: number[];
  heart_scores?: MetricSource;
  cardiovascular?: MetricSource;
  glucose_info?: MetricSource;
}

interface SkinParameter {
  score: number;
  maximum_score: number;
  comment: string;
  label: string;
}

interface SkinReport {
  skin_score: number;
  skin_grade: string;
  estimated_skin_age: number;
  parameters?: Record<string, SkinParameter>;
  summary_and_recommendations?: Partial<Record<SkinRoutineTitle, string[]>>;
}

interface ScanResultsData {
  vitals?: MetricSource;
  original_vitals?: MetricSource;
  metadata?: ScanMetadata;
  skinScan?: {
    skin_report?: SkinReport;
  };
  scan_id?: string;
  request_id?: string;
  wellness_score?: MetricValue;
  user_name?: string;
  name?: string;
  age?: MetricValue;
  gender?: string;
  scan_completion_time?: MetricValue;
  timestamp?: MetricValue;
  scan_date?: MetricValue;
}

interface ScanResultsProps {
  data: ScanResultsData;
  userData?: UserProfile | null;
  onNewScan?: () => void;
  onDownload?: () => void;
}

interface MetricRange {
  min: number;
  max: number;
  label: string;
  badge: MetricBadge;
}

interface MetricInfo {
  name: string;
  value: number;
  unit: string;
  rangeText: string;
  badge: MetricBadge;
  detailedLabel: string;
  meaning: string;
  suggestion: string;
  category: MetricCategory;
  icon: string;
}

interface InsightItem {
  title: string;
  text: string;
  tone: StatusTone;
}

interface ActionPlanCard {
  title: 'Today' | 'This Week' | 'Medical Confirmation';
  tone: StatusTone;
  items: string[];
}

interface CircularScoreProps {
  score: number;
  label: string;
  tone: StatusTone;
  size?: number;
  suffix?: string;
  secondaryText?: string;
}

interface SparklineProps {
  values: number[];
  tone?: StatusTone;
}

interface RangeBarProps {
  metricName: string;
  value: number;
}

const metricDisplayOrder = [
  'Health Score',
  'Heart Rate',
  'BP Systolic',
  'BP Diastolic',
  'Oxygen Saturation (SPO2)',
  'Respiration Rate',
  'BMI',
  'Haemoglobin',
  'Hba1c',
  'Stress Index',
  'Hypertension Risk',
  'Hba1c Risk',
  'Mean Arterial Pressure (MAP)',
  'Body Fat Percentage',
  'Total Body Water',
  'Blood Volume',
  'Heart Utilised',
  'Cardiac Load Risk',
  'HRV',
  'VO2 Max',
  'Heart Rate Max',
  'Heart Rate Reserve',
  'Cardiac Output',
  'BMR',
  'TDEE',
  'SDNN',
  'RMSSD',
  'PNN50'
];

const metricDisplayOrderLookup = new Map(metricDisplayOrder.map((metricName, index) => [metricName, index]));

const categoryOrder: MetricCategory[] = [
  'Vital Signs',
  'Heart & Cardiovascular',
  'Metabolic',
  'Fitness & Body Composition',
  'HRV / Recovery'
];

const categoryDescriptions: Record<MetricCategory, string> = {
  'Vital Signs': 'Core face scan readings captured during this session.',
  'Heart & Cardiovascular': 'Circulation, blood pressure, and heart workload indicators.',
  'Metabolic': 'Glucose-related and blood chemistry guidance markers.',
  'Fitness & Body Composition': 'Body composition and energy-use estimates from the scan.',
  'HRV / Recovery': 'Recovery balance, stress response, and autonomic signals.'
};

const tonePalette: Record<StatusTone, { base: string; soft: string; border: string; track: string; text: string }> = {
  success: {
    base: '#2B7A56',
    soft: '#EDF7F1',
    border: '#CDE3D5',
    track: '#DCEEE4',
    text: '#163826'
  },
  warning: {
    base: '#A86B19',
    soft: '#FFF6E8',
    border: '#EACF9D',
    track: '#F5E4C1',
    text: '#5C3A07'
  },
  danger: {
    base: '#B24A4A',
    soft: '#FCEEEE',
    border: '#EDC6C6',
    track: '#F5D9D9',
    text: '#5F2020'
  },
  info: {
    base: '#2C6C95',
    soft: '#EEF6FB',
    border: '#C4DBEA',
    track: '#D6E8F3',
    text: '#17384E'
  },
  skin: {
    base: '#7D58A9',
    soft: '#F5EFFB',
    border: '#DACBED',
    track: '#E7DCF6',
    text: '#432A5D'
  },
  neutral: {
    base: '#667085',
    soft: '#F5F7FA',
    border: '#DDE3EC',
    track: '#E8EDF3',
    text: '#334155'
  }
};

const metricRanges: Record<string, MetricRange[]> = {
  SDNN: [
    { min: 0, max: 30, label: 'Low', badge: 'LOW' },
    { min: 30, max: 96, label: 'Healthy', badge: 'NORMAL' },
    { min: 96, max: 150, label: 'Optimal', badge: 'NORMAL' }
  ],
  RMSSD: [
    { min: 0, max: 20, label: 'Low', badge: 'LOW' },
    { min: 20, max: 90, label: 'Healthy', badge: 'NORMAL' },
    { min: 90, max: 150, label: 'Optimal', badge: 'NORMAL' }
  ],
  PNN50: [
    { min: 0, max: 3, label: 'Low', badge: 'LOW' },
    { min: 3, max: 20, label: 'Normal', badge: 'NORMAL' },
    { min: 20, max: 90, label: 'Optimal', badge: 'NORMAL' }
  ],
  'Cardiac Output': [
    { min: 0, max: 4, label: 'Low', badge: 'LOW' },
    { min: 4, max: 8, label: 'Average', badge: 'NORMAL' },
    { min: 8, max: 15, label: 'Normal', badge: 'NORMAL' }
  ],
  'Mean Arterial Pressure (MAP)': [
    { min: 0, max: 70, label: 'Low', badge: 'LOW' },
    { min: 70, max: 100, label: 'Normal', badge: 'NORMAL' },
    { min: 100, max: 150, label: 'High', badge: 'HIGH' }
  ],
  HRV: [
    { min: 0, max: 33, label: 'Low', badge: 'LOW' },
    { min: 33, max: 66, label: 'Normal', badge: 'NORMAL' },
    { min: 66, max: 100, label: 'Good', badge: 'NORMAL' }
  ],
  'Heart Rate': [
    { min: 30, max: 55, label: 'Low', badge: 'LOW' },
    { min: 55, max: 100, label: 'Normal', badge: 'NORMAL' },
    { min: 100, max: 220, label: 'High', badge: 'HIGH' }
  ],
  'Oxygen Saturation (SPO2)': [
    { min: 80, max: 91, label: 'Very Low', badge: 'LOW' },
    { min: 91, max: 94, label: 'Low', badge: 'LOW' },
    { min: 94, max: 100, label: 'Normal', badge: 'NORMAL' }
  ],
  'BP Diastolic': [
    { min: 30, max: 70, label: 'Low', badge: 'LOW' },
    { min: 70, max: 89, label: 'Normal', badge: 'NORMAL' },
    { min: 89, max: 120, label: 'High', badge: 'HIGH' }
  ],
  'BP Systolic': [
    { min: 50, max: 100, label: 'Low', badge: 'LOW' },
    { min: 100, max: 120, label: 'Normal', badge: 'NORMAL' },
    { min: 120, max: 220, label: 'High', badge: 'HIGH' }
  ],
  'Hba1c Risk': [
    { min: 0, max: 30, label: 'Low', badge: 'NORMAL' },
    { min: 30, max: 70, label: 'Moderate', badge: 'HIGH' },
    { min: 70, max: 100, label: 'Severe', badge: 'HIGH' }
  ],
  'Stress Index': [
    { min: 0, max: 0.5, label: 'Low', badge: 'NORMAL' },
    { min: 0.5, max: 1.5, label: 'Normal', badge: 'NORMAL' },
    { min: 1.5, max: 5, label: 'Elevated', badge: 'HIGH' },
    { min: 5, max: 9, label: 'High', badge: 'HIGH' },
    { min: 9, max: 100, label: 'Very High', badge: 'HIGH' }
  ],
  'Respiration Rate': [
    { min: 6, max: 12, label: 'Low', badge: 'LOW' },
    { min: 12, max: 21, label: 'Normal', badge: 'NORMAL' },
    { min: 21, max: 30, label: 'High', badge: 'HIGH' }
  ],
  'Hypertension Risk': [
    { min: 0, max: 30, label: 'Low', badge: 'NORMAL' },
    { min: 30, max: 70, label: 'Moderate', badge: 'HIGH' },
    { min: 70, max: 100, label: 'Severe', badge: 'HIGH' }
  ],
  Haemoglobin: [
    { min: 0, max: 13, label: 'Low', badge: 'LOW' },
    { min: 13, max: 17, label: 'Normal', badge: 'NORMAL' },
    { min: 17, max: 20, label: 'High', badge: 'HIGH' }
  ],
  Hba1c: [
    { min: 0, max: 5.7, label: 'Normal', badge: 'NORMAL' },
    { min: 5.7, max: 6.5, label: 'Elevated', badge: 'HIGH' },
    { min: 6.5, max: 12, label: 'High', badge: 'HIGH' }
  ],
  'Health Score': [
    { min: 0, max: 25, label: 'Poor', badge: 'LOW' },
    { min: 25, max: 50, label: 'Fair', badge: 'LOW' },
    { min: 50, max: 75, label: 'Good', badge: 'NORMAL' },
    { min: 75, max: 100, label: 'Great', badge: 'NORMAL' }
  ],
  'Heart Utilised': [
    { min: 0, max: 50, label: 'Low', badge: 'NORMAL' },
    { min: 50, max: 65, label: 'Normal', badge: 'NORMAL' },
    { min: 65, max: 100, label: 'High', badge: 'HIGH' }
  ],
  'Cardiac Load Risk': [
    { min: 0, max: 30, label: 'Low', badge: 'NORMAL' },
    { min: 30, max: 70, label: 'Moderate', badge: 'HIGH' },
    { min: 70, max: 100, label: 'High', badge: 'HIGH' }
  ],
  BMI: [
    { min: 0, max: 18.4, label: 'Low', badge: 'LOW' },
    { min: 18.4, max: 24.9, label: 'Normal', badge: 'NORMAL' },
    { min: 24.9, max: 29.9, label: 'Overweight', badge: 'HIGH' },
    { min: 29.9, max: 40, label: 'Obese', badge: 'HIGH' }
  ],
  'Body Fat Percentage': [
    { min: 0, max: 8, label: 'Low', badge: 'LOW' },
    { min: 8, max: 20, label: 'Normal', badge: 'NORMAL' },
    { min: 20, max: 100, label: 'High', badge: 'HIGH' }
  ],
  BMR: [
    { min: 1000, max: 1399, label: 'Low', badge: 'LOW' },
    { min: 1399, max: 1799, label: 'Normal', badge: 'NORMAL' },
    { min: 1799, max: 2299, label: 'High', badge: 'NORMAL' },
    { min: 2299, max: 3000, label: 'Highest', badge: 'NORMAL' }
  ],
  TDEE: [
    { min: 1500, max: 1999, label: 'Poor', badge: 'LOW' },
    { min: 1999, max: 2499, label: 'Normal', badge: 'NORMAL' },
    { min: 2499, max: 3199, label: 'Good', badge: 'NORMAL' },
    { min: 3199, max: 4500, label: 'Great', badge: 'NORMAL' }
  ],
  'Total Body Water': [
    { min: 0, max: 45, label: 'Low', badge: 'LOW' },
    { min: 45, max: 60, label: 'Moderate', badge: 'NORMAL' },
    { min: 60, max: 100, label: 'Optimal', badge: 'NORMAL' }
  ],
  'Blood Volume': [
    { min: 0, max: 4, label: 'Low', badge: 'LOW' },
    { min: 4, max: 6, label: 'Normal', badge: 'NORMAL' },
    { min: 6, max: 10, label: 'High', badge: 'NORMAL' }
  ],
  'Heart Rate Max': [
    { min: 140, max: 160, label: 'Low', badge: 'NORMAL' },
    { min: 160, max: 180, label: 'Normal', badge: 'NORMAL' },
    { min: 180, max: 220, label: 'High', badge: 'NORMAL' }
  ],
  'Heart Rate Reserve': [
    { min: 0, max: 40, label: 'Low', badge: 'LOW' },
    { min: 40, max: 80, label: 'Normal', badge: 'NORMAL' },
    { min: 80, max: 150, label: 'High', badge: 'NORMAL' }
  ],
  'Target HR Rate': [
    { min: 90, max: 120, label: 'Low', badge: 'NORMAL' },
    { min: 120, max: 150, label: 'Moderate', badge: 'NORMAL' },
    { min: 150, max: 170, label: 'High', badge: 'NORMAL' }
  ],
  'VO2 Max': [
    { min: 0, max: 35, label: 'Poor', badge: 'LOW' },
    { min: 35, max: 45, label: 'Fair', badge: 'LOW' },
    { min: 45, max: 55, label: 'Good', badge: 'NORMAL' },
    { min: 55, max: 80, label: 'Excellent', badge: 'NORMAL' }
  ]
};

const metricMapping: Record<string, { displayName: string; unit: string }> = {
  hrv_sdnn: { displayName: 'SDNN', unit: 'ms' },
  hrv_rmssd: { displayName: 'RMSSD', unit: 'ms' },
  pNN50_per: { displayName: 'PNN50', unit: '%' },
  cardiac_out: { displayName: 'Cardiac Output', unit: 'L/min' },
  map: { displayName: 'Mean Arterial Pressure (MAP)', unit: 'mmHg' },
  hrvScore: { displayName: 'HRV', unit: '' },
  heart_rate: { displayName: 'Heart Rate', unit: 'bpm' },
  heart_rate_bpm: { displayName: 'Heart Rate', unit: 'bpm' },
  oxy_sat_prcnt: { displayName: 'Oxygen Saturation (SPO2)', unit: '%' },
  spo2: { displayName: 'Oxygen Saturation (SPO2)', unit: '%' },
  bp_dia: { displayName: 'BP Diastolic', unit: 'mmHg' },
  dbp: { displayName: 'BP Diastolic', unit: 'mmHg' },
  bp_sys: { displayName: 'BP Systolic', unit: 'mmHg' },
  sbp: { displayName: 'BP Systolic', unit: 'mmHg' },
  hba1c_risk_percentage: { displayName: 'Hba1c Risk', unit: '%' },
  stress: { displayName: 'Stress Index', unit: '' },
  stress_index: { displayName: 'Stress Index', unit: '' },
  resp_rate: { displayName: 'Respiration Rate', unit: 'brpm' },
  respiration_rate_bpm: { displayName: 'Respiration Rate', unit: 'brpm' },
  hypertension_risk_percentage: { displayName: 'Hypertension Risk', unit: '%' },
  hemoglobin: { displayName: 'Haemoglobin', unit: 'g/dL' },
  Hemoglobin: { displayName: 'Haemoglobin', unit: 'g/dL' },
  hba1c: { displayName: 'Hba1c', unit: '%' },
  overall_health_score: { displayName: 'Health Score', unit: '' },
  heart_utilized: { displayName: 'Heart Utilised', unit: '%' },
  cardiac_load_risk_percentage: { displayName: 'Cardiac Load Risk', unit: '%' },
  bmi: { displayName: 'BMI', unit: '' },
  bodyfat: { displayName: 'Body Fat Percentage', unit: '%' },
  bmr: { displayName: 'BMR', unit: 'kcal/day' },
  tdee: { displayName: 'TDEE', unit: 'kcal/day' },
  tbwp: { displayName: 'Total Body Water', unit: '%' },
  bloodvolume: { displayName: 'Blood Volume', unit: 'L' },
  HRMax: { displayName: 'Heart Rate Max', unit: 'bpm' },
  HRR: { displayName: 'Heart Rate Reserve', unit: 'bpm' },
  vo2max: { displayName: 'VO2 Max', unit: 'mL/kg/min' }
};

const metricCategoryMap: Record<string, MetricCategory> = {
  'Health Score': 'Vital Signs',
  'Heart Rate': 'Vital Signs',
  'BP Systolic': 'Vital Signs',
  'BP Diastolic': 'Vital Signs',
  'Oxygen Saturation (SPO2)': 'Vital Signs',
  'Respiration Rate': 'Vital Signs',
  'Haemoglobin': 'Vital Signs',
  'Mean Arterial Pressure (MAP)': 'Heart & Cardiovascular',
  'Hypertension Risk': 'Heart & Cardiovascular',
  'Heart Utilised': 'Heart & Cardiovascular',
  'Cardiac Load Risk': 'Heart & Cardiovascular',
  'Cardiac Output': 'Heart & Cardiovascular',
  'Heart Rate Max': 'Heart & Cardiovascular',
  'Heart Rate Reserve': 'Heart & Cardiovascular',
  Hba1c: 'Metabolic',
  'Hba1c Risk': 'Metabolic',
  BMI: 'Fitness & Body Composition',
  'Body Fat Percentage': 'Fitness & Body Composition',
  'Total Body Water': 'Fitness & Body Composition',
  'Blood Volume': 'Fitness & Body Composition',
  BMR: 'Fitness & Body Composition',
  TDEE: 'Fitness & Body Composition',
  'VO2 Max': 'Fitness & Body Composition',
  HRV: 'HRV / Recovery',
  SDNN: 'HRV / Recovery',
  RMSSD: 'HRV / Recovery',
  PNN50: 'HRV / Recovery',
  'Stress Index': 'HRV / Recovery'
};

const metricIconMap: Record<string, string> = {
  'Health Score': '🧭',
  'Heart Rate': '❤️',
  'BP Systolic': '🩺',
  'BP Diastolic': '🩺',
  'Oxygen Saturation (SPO2)': '🫁',
  'Respiration Rate': '🌬️',
  BMI: '⚖️',
  Haemoglobin: '🩸',
  Hba1c: '🧪',
  'Stress Index': '🧘',
  'Hypertension Risk': '🫀',
  'Hba1c Risk': '🧪',
  'Mean Arterial Pressure (MAP)': '🫀',
  'Body Fat Percentage': '📏',
  'Total Body Water': '💧',
  'Blood Volume': '🩸',
  'Heart Utilised': '💓',
  'Cardiac Load Risk': '🫀',
  HRV: '🫶',
  'VO2 Max': '🏃',
  'Heart Rate Max': '🏁',
  'Heart Rate Reserve': '⚡',
  'Cardiac Output': '🚿',
  BMR: '🔥',
  TDEE: '🍽️',
  SDNN: '🛌',
  RMSSD: '🛌',
  PNN50: '🛌'
};

const metricDecimalPlaces: Record<string, number> = {
  BMI: 1,
  Hba1c: 1,
  'Stress Index': 1,
  'Body Fat Percentage': 1,
  'Total Body Water': 1,
  'Blood Volume': 1,
  'Cardiac Output': 1,
  'VO2 Max': 1
};

const defaultSkinRecommendations: Record<SkinRoutineTitle, string[]> = {
  'Morning Routine': [
    'Use a gentle cleanser and avoid stripping the skin barrier.',
    'Apply a hydrating serum followed by a lightweight moisturizer.',
    'Finish with broad-spectrum SPF 30+ every morning.'
  ],
  'Evening Routine': [
    'Cleanse thoroughly to remove sunscreen, oil, and makeup buildup.',
    'Use a calming treatment that matches your lowest scoring concern.',
    'Seal in moisture with a restorative evening cream.'
  ],
  'Weekly Treatments': [
    'Use one exfoliating or clarifying treatment only if skin tolerates it well.',
    'Add a hydrating mask or barrier-repair treatment once or twice weekly.',
    'Avoid layering too many strong actives on the same day.'
  ],
  'Lifestyle Tips': [
    'Prioritize sleep, hydration, and steady stress management.',
    'Keep pillowcases, towels, and phone surfaces clean.',
    'Track skin changes gradually rather than reacting to one scan alone.'
  ]
};

const skinRoutineIcons: Record<SkinRoutineTitle, string> = {
  'Morning Routine': '🌤️',
  'Evening Routine': '🌙',
  'Weekly Treatments': '🧴',
  'Lifestyle Tips': '🍃'
};

const skinParameterMap: Record<string, { label: string; icon: string }> = {
  wrinkles: { label: 'Wrinkles', icon: '✨' },
  unevenskintone: { label: 'Uneven Skin Tone', icon: '🎨' },
  redness: { label: 'Redness', icon: '🌿' },
  pores: { label: 'Pores', icon: '🫧' },
  oilintensity: { label: 'Oil Intensity', icon: '💧' },
  acne: { label: 'Acne', icon: '🩹' },
  blackheads: { label: 'Blackheads', icon: '⚫' },
  darkcircles: { label: 'Dark Circles', icon: '🌙' },
  dehydration: { label: 'Dehydration', icon: '💦' }
};

const parseNumericValue = (value: MetricValue): number | null => {
  if (value === null || value === undefined || value === '' || value === '--') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const parsed = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const parseDateValue = (value: MetricValue): Date | null => {
  if (value === null || value === undefined || value === '' || value === '--') {
    return null;
  }

  if (typeof value === 'number') {
    const timestamp = value > 9999999999 ? value : value * 1000;
    const parsedDate = new Date(timestamp);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const numericStringValue = Number(String(value));
  if (!Number.isNaN(numericStringValue) && String(value).trim() !== '') {
    const timestamp = numericStringValue > 9999999999 ? numericStringValue : numericStringValue * 1000;
    const parsedDate = new Date(timestamp);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const parsedDate = new Date(String(value));
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatValue = (value: MetricValue, decimals = 0): string => {
  const numericValue = parseNumericValue(value);
  if (numericValue === null) {
    return '--';
  }

  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const getStatusTone = (label: string): StatusTone => {
  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel.includes('very low') || normalizedLabel.includes('very high') || normalizedLabel.includes('severe') || normalizedLabel.includes('obese')) {
    return 'danger';
  }

  if (normalizedLabel.includes('high')) {
    return 'danger';
  }

  if (
    normalizedLabel.includes('low') ||
    normalizedLabel.includes('moderate') ||
    normalizedLabel.includes('fair') ||
    normalizedLabel.includes('average') ||
    normalizedLabel.includes('elevated') ||
    normalizedLabel.includes('poor') ||
    normalizedLabel.includes('overweight')
  ) {
    return 'warning';
  }

  if (
    normalizedLabel.includes('normal') ||
    normalizedLabel.includes('healthy') ||
    normalizedLabel.includes('optimal') ||
    normalizedLabel.includes('good') ||
    normalizedLabel.includes('great') ||
    normalizedLabel.includes('excellent')
  ) {
    return 'success';
  }

  return 'info';
};

const getMetricCategory = (metricName: string): MetricCategory => metricCategoryMap[metricName] || 'Vital Signs';

const getMetricIcon = (metricName: string): string => metricIconMap[metricName] || '📊';

const getMetricDecimals = (metricName: string, value?: number): number => {
  if (metricDecimalPlaces[metricName] !== undefined) {
    return metricDecimalPlaces[metricName];
  }

  if (value !== undefined && !Number.isInteger(value)) {
    return 1;
  }

  return 0;
};

const getScoreTone = (score: number, variant: 'wellness' | 'skin' = 'wellness'): StatusTone => {
  if (variant === 'skin') {
    if (score >= 80) return 'skin';
    if (score >= 60) return 'warning';
    return 'danger';
  }

  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
};

const getWellnessSummary = (score: number, needsAttentionCount: number, totalMetrics: number) => {
  if (score >= 80) {
    return {
      label: 'Great',
      tone: 'success' as StatusTone,
      text: `The overall pattern looks strong for this scan. ${needsAttentionCount} of ${totalMetrics} metrics still deserve routine follow-up, but the broad profile is encouraging.`
    };
  }

  if (score >= 60) {
    return {
      label: 'Good',
      tone: 'warning' as StatusTone,
      text: `The scan shows a generally steady profile with a few areas to monitor. ${needsAttentionCount} of ${totalMetrics} metrics would benefit from a repeat scan or lifestyle review.`
    };
  }

  return {
    label: 'Needs Attention',
    tone: 'danger' as StatusTone,
    text: `Several readings are outside their preferred ranges in this scan. Recheck under calm, consistent conditions and confirm any persistent flags with standard medical testing when appropriate.`
  };
};

const normaliseTextKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, '');

const formatGender = (value?: string): string => {
  if (!value) {
    return '--';
  }

  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const getRangeTone = (range: MetricRange): StatusTone => {
  if (range.badge === 'NORMAL') {
    return 'success';
  }

  return getStatusTone(range.label);
};

const classifyMetric = (metricName: string, value: number): { detailedLabel: string; badge: MetricBadge } => {
  const ranges = metricRanges[metricName];
  if (!ranges || !ranges.length) {
    return { detailedLabel: 'Reference Pending', badge: 'NORMAL' };
  }

  for (let index = 0; index < ranges.length; index += 1) {
    const range = ranges[index];
    const isLastRange = index === ranges.length - 1;
    const isWithinRange = (value >= range.min && value < range.max) || (isLastRange && value >= range.min && value <= range.max);

    if (isWithinRange) {
      return { detailedLabel: range.label, badge: range.badge };
    }
  }

  const lastRange = ranges[ranges.length - 1];
  return {
    detailedLabel: lastRange.label,
    badge: lastRange.badge === 'NORMAL' ? 'HIGH' : lastRange.badge
  };
};

const getRangeText = (metricName: string): string => {
  const ranges = metricRanges[metricName];
  if (!ranges) {
    return 'Reference range not available';
  }

  return ranges
    .map((range) => `${range.label}: ${formatValue(range.min, range.min < 1 ? 1 : 0)}-${formatValue(range.max, range.max < 1 ? 1 : 0)}`)
    .join(' | ');
};

const getMetricTone = (metric: MetricInfo): StatusTone => {
  if (metric.badge === 'NORMAL') {
    return metric.category === 'Fitness & Body Composition' ? 'info' : 'success';
  }

  return getStatusTone(metric.detailedLabel);
};

const getMeaningAndSuggestion = (metricName: string, badge: MetricBadge, detailedLabel: string): { meaning: string; suggestion: string } => {
  const guidance: Record<string, Record<string, { meaning: string; suggestion: string }>> = {
    'Heart Rate': {
      LOW: {
        meaning: 'Resting heart rate is lower than the typical range for this scan.',
        suggestion: 'Repeat when fully awake and hydrated, and discuss with a clinician if low readings persist with symptoms.'
      },
      HIGH: {
        meaning: 'Resting heart rate is above the expected range for a calm scan.',
        suggestion: 'Rest, hydrate, and repeat in a relaxed setting; consider clinical review if it stays elevated.'
      }
    },
    'Oxygen Saturation (SPO2)': {
      'Very Low': {
        meaning: 'Oxygen saturation appears well below the preferred range in this scan.',
        suggestion: 'Repeat with warm hands and steady breathing, and seek prompt medical review if low readings or symptoms continue.'
      },
      LOW: {
        meaning: 'Oxygen saturation appears lower than expected on this reading.',
        suggestion: 'Repeat at rest and confirm with a pulse oximeter or clinician if the result stays low.'
      }
    },
    'BP Systolic': {
      LOW: {
        meaning: 'Systolic pressure trends lower than the expected range in this scan.',
        suggestion: 'Hydrate well, stand up slowly, and repeat later if you felt tired or light-headed during the scan.'
      },
      HIGH: {
        meaning: 'Systolic pressure trends above the preferred range in this scan.',
        suggestion: 'Check with a validated BP cuff over the next few days and review with a clinician if readings remain high.'
      }
    },
    'BP Diastolic': {
      LOW: {
        meaning: 'Diastolic pressure is lower than expected in this scan.',
        suggestion: 'Hydrate, avoid sudden position changes, and repeat when feeling well rested.'
      },
      HIGH: {
        meaning: 'Diastolic pressure trends above the preferred range in this scan.',
        suggestion: 'Reduce excess sodium, prioritize recovery, and confirm with cuff readings if this remains elevated.'
      }
    },
    Hba1c: {
      Elevated: {
        meaning: 'HbA1c estimate is in an elevated range that may warrant closer review.',
        suggestion: 'Review meals, activity, and consider a lab-based HbA1c test if future scans show a similar result.'
      },
      High: {
        meaning: 'HbA1c estimate is in a higher-risk range on this scan.',
        suggestion: 'Arrange lab confirmation and discuss next steps with a doctor if this pattern repeats.'
      }
    },
    'Hba1c Risk': {
      Moderate: {
        meaning: 'HbA1c-related risk is moderate in this screening snapshot.',
        suggestion: 'Focus on meal quality, regular movement, and consider lab confirmation if the next scan stays elevated.'
      },
      Severe: {
        meaning: 'HbA1c-related risk is elevated in this screening snapshot.',
        suggestion: 'Use a formal lab test and clinical review to confirm the result and plan follow-up.'
      }
    },
    'Hypertension Risk': {
      Moderate: {
        meaning: 'Hypertension risk is moderate based on this scan pattern.',
        suggestion: 'Monitor blood pressure with a validated cuff and repeat the scan under calm conditions.'
      },
      Severe: {
        meaning: 'Hypertension risk appears elevated in this scan.',
        suggestion: 'Confirm with standard BP measurements and arrange clinician review if repeat readings remain high.'
      }
    },
    'Stress Index': {
      Elevated: {
        meaning: 'Stress load appears above the preferred day-of-scan range.',
        suggestion: 'Prioritize sleep, hydration, and a quiet recheck environment before the next scan.'
      },
      High: {
        meaning: 'Stress markers suggest the body may be under notable load today.',
        suggestion: 'Build in recovery time, reduce stimulants before rechecking, and compare again after rest.'
      },
      'Very High': {
        meaning: 'Stress markers are strongly elevated in this scan.',
        suggestion: 'Pause, recover, and repeat the scan later; seek professional advice if stress symptoms feel persistent.'
      }
    },
    HRV: {
      LOW: {
        meaning: 'HRV suggests recovery balance may be reduced today.',
        suggestion: 'Sleep quality, hydration, and easier training days may help before the next scan.'
      }
    },
    SDNN: {
      LOW: {
        meaning: 'SDNN is lower than the preferred recovery range.',
        suggestion: 'Use a calm retest window and focus on recovery habits such as sleep and stress management.'
      }
    },
    RMSSD: {
      LOW: {
        meaning: 'RMSSD suggests reduced parasympathetic recovery at the time of the scan.',
        suggestion: 'A calmer recheck, better sleep, and lower day-of-scan stress may improve the next reading.'
      }
    },
    BMI: {
      LOW: {
        meaning: 'BMI estimate sits below the usual reference range.',
        suggestion: 'Review nutrition and body composition goals with a clinician or nutrition professional if this is unexpected.'
      },
      Overweight: {
        meaning: 'BMI estimate suggests there may be extra body weight for height.',
        suggestion: 'Use balanced nutrition and regular activity to monitor trends rather than reacting to a single scan.'
      },
      Obese: {
        meaning: 'BMI estimate is in a higher range and may warrant closer follow-up.',
        suggestion: 'A structured nutrition, movement, and clinician-guided review can help confirm and plan next steps.'
      }
    },
    Haemoglobin: {
      LOW: {
        meaning: 'Hemoglobin estimate is below the preferred range in this screening snapshot.',
        suggestion: 'Consider a lab-based blood test if this repeats, especially if fatigue or shortness of breath is present.'
      },
      HIGH: {
        meaning: 'Hemoglobin estimate is above the preferred range in this screening snapshot.',
        suggestion: 'Hydrate well and use a lab test for confirmation if future scans stay outside range.'
      }
    },
    'VO2 Max': {
      Poor: {
        meaning: 'Estimated aerobic fitness is below the preferred range.',
        suggestion: 'Build cardiovascular activity gradually and compare progress with repeat scans over time.'
      },
      Fair: {
        meaning: 'Estimated aerobic fitness has room to improve.',
        suggestion: 'Progressively increase walking, cycling, or light cardio sessions during the week.'
      }
    },
    'Total Body Water': {
      LOW: {
        meaning: 'Hydration estimate is lower than preferred on this scan.',
        suggestion: 'Hydrate consistently through the day and avoid measuring again when dehydrated.'
      }
    }
  };

  const metricGuidance = guidance[metricName];
  if (metricGuidance) {
    const specificGuidance = metricGuidance[detailedLabel] || metricGuidance[badge];
    if (specificGuidance) {
      return specificGuidance;
    }
  }

  if (badge === 'LOW') {
    return {
      meaning: `${metricName} is below the preferred range in this scan.`,
      suggestion: 'Repeat in consistent conditions and use clinical confirmation if the result remains outside range.'
    };
  }

  if (badge === 'HIGH') {
    return {
      meaning: `${metricName} is above the preferred range in this scan.`,
      suggestion: 'Review lifestyle factors, repeat under calm conditions, and confirm with a clinician if the finding persists.'
    };
  }

  return {
    meaning: `This reading sits in the ${detailedLabel.toLowerCase()} range for this scan.`,
    suggestion: 'Maintain current habits and use repeat scans to watch for consistent trends over time.'
  };
};

const processMetrics = (data: ScanResultsData): Record<string, MetricInfo> => {
  const mergedMetrics: MetricSource = {
    ...(data.original_vitals || {}),
    ...(data.metadata?.physiological_scores || {}),
    ...(data.metadata?.heart_scores || {}),
    ...(data.metadata?.cardiovascular || {}),
    ...(data.metadata?.glucose_info || {}),
    ...(data.vitals || {})
  };

  const metrics: Record<string, MetricInfo> = {};
  const processedDisplayNames = new Set<string>();

  Object.entries(metricMapping).forEach(([apiField, config]) => {
    if (processedDisplayNames.has(config.displayName)) {
      return;
    }

    const numericValue = parseNumericValue(mergedMetrics[apiField]);
    if (numericValue === null) {
      return;
    }

    const classification = classifyMetric(config.displayName, numericValue);
    const guidance = getMeaningAndSuggestion(config.displayName, classification.badge, classification.detailedLabel);

    metrics[config.displayName] = {
      name: config.displayName,
      value: numericValue,
      unit: config.unit,
      rangeText: getRangeText(config.displayName),
      badge: classification.badge,
      detailedLabel: classification.detailedLabel,
      meaning: guidance.meaning,
      suggestion: guidance.suggestion,
      category: getMetricCategory(config.displayName),
      icon: getMetricIcon(config.displayName)
    };

    processedDisplayNames.add(config.displayName);
  });

  return metrics;
};

const buildMetricInsight = (metric: MetricInfo, positive = false): InsightItem => {
  const label = metric.detailedLabel.toLowerCase();
  const tone = positive ? (metric.category === 'Fitness & Body Composition' ? 'info' : 'success') : getMetricTone(metric);

  switch (metric.name) {
    case 'Hba1c':
    case 'Hba1c Risk':
      return {
        title: metric.name,
        text: positive
          ? `${metric.name} looks ${label} in this scan, which is reassuring. Continue steady meals, activity, and periodic follow-up.`
          : `${metric.name} is in the ${label} range. Consider a diet review and confirm with a doctor or lab test if this stays elevated.`,
        tone
      };
    case 'Hypertension Risk':
    case 'BP Systolic':
    case 'BP Diastolic':
      return {
        title: metric.name,
        text: positive
          ? `${metric.name} sits ${label} in this scan, supporting a steadier cardiovascular picture today.`
          : `${metric.name} is in the ${label} range. Monitor blood pressure regularly and confirm with standard measurements if repeat scans stay outside range.`,
        tone
      };
    case 'Oxygen Saturation (SPO2)':
      return {
        title: metric.name,
        text: positive
          ? `Oxygen saturation is ${label} in this scan, which is a reassuring finding when compared over time.`
          : `Oxygen saturation is ${label} in this scan. Repeat at rest and seek medical confirmation if low values continue or symptoms are present.`,
        tone
      };
    case 'Stress Index':
      return {
        title: metric.name,
        text: positive
          ? `Stress level appears ${label} in this scan, which supports good day-of-scan recovery balance.`
          : `Stress level appears ${label} in this scan. Prioritize sleep, hydration, and a calmer environment before the next check.`,
        tone
      };
    case 'HRV':
    case 'SDNN':
    case 'RMSSD':
      return {
        title: metric.name,
        text: positive
          ? `${metric.name} is ${label}, suggesting recovery markers look steady today.`
          : `${metric.name} is ${label}, which may reflect recovery load. Use rest, hydration, and a calmer retest window before comparing again.`,
        tone
      };
    case 'Haemoglobin':
      return {
        title: metric.name,
        text: positive
          ? `Hemoglobin estimate is ${label} in this scan.`
          : `Hemoglobin estimate is ${label} in this scan. Consider lab confirmation if this repeats, especially with fatigue or shortness of breath.`,
        tone
      };
    case 'VO2 Max':
      return {
        title: metric.name,
        text: positive
          ? `Estimated aerobic fitness is ${label}, which supports a stronger activity base.`
          : `Estimated aerobic fitness is ${label}. Regular cardio and gradual conditioning may help improve future results.`,
        tone
      };
    default:
      return {
        title: metric.name,
        text: positive
          ? `${metric.name} is ${label} in this scan. Keep the same healthy routine and compare with future scans.`
          : `${metric.name} is in the ${label} range. ${metric.suggestion}`,
        tone
      };
  }
};

const buildTopInsights = (metrics: MetricInfo[]): InsightItem[] => {
  if (!metrics.length) {
    return [
      {
        title: 'Scan Summary',
        text: 'No structured metric set was available to summarize for this report.',
        tone: 'info'
      }
    ];
  }

  const priorityOrder = [
    'Hba1c',
    'Hba1c Risk',
    'Hypertension Risk',
    'BP Systolic',
    'BP Diastolic',
    'Oxygen Saturation (SPO2)',
    'Stress Index',
    'HRV',
    'Haemoglobin',
    'Heart Rate',
    'VO2 Max',
    'BMI',
    'Total Body Water'
  ];

  const metricLookup = new Map(metrics.map((metric) => [metric.name, metric]));
  const abnormalCandidates = priorityOrder
    .map((metricName) => metricLookup.get(metricName))
    .filter((metric): metric is MetricInfo => metric !== undefined && metric.badge !== 'NORMAL');

  const positiveCandidates = ['Stress Index', 'Oxygen Saturation (SPO2)', 'Heart Rate', 'HRV', 'VO2 Max', 'Health Score']
    .map((metricName) => metricLookup.get(metricName))
    .filter((metric): metric is MetricInfo => metric !== undefined && metric.badge === 'NORMAL');

  const insights: InsightItem[] = abnormalCandidates.slice(0, 5).map((metric) => buildMetricInsight(metric));

  positiveCandidates.forEach((metric) => {
    if (insights.length < 3 && !insights.some((insight) => insight.title === metric.name)) {
      insights.push(buildMetricInsight(metric, true));
    }
  });

  if (!insights.length) {
    return metrics.slice(0, 3).map((metric) => buildMetricInsight(metric, true));
  }

  return insights.slice(0, 5);
};

const buildRecommendedActions = (metrics: MetricInfo[], skinReport?: SkinReport): ActionPlanCard[] => {
  const hasMetricFlag = (metricNames: string[]) => metrics.some((metric) => metricNames.includes(metric.name) && metric.badge !== 'NORMAL');

  const todayItems = [
    'Hydrate well, eat normally, and prioritize rest before any repeat scan.',
    'Avoid intense exercise, stimulants, or stress right before a recheck.',
    'Use this report as a screening snapshot rather than a diagnosis.'
  ];

  if (skinReport) {
    todayItems.push('Follow the skin routine cards consistently, especially for the lowest scoring focus areas.');
  }

  const weekItems = [
    'Repeat the scan under similar conditions to compare trends more reliably.',
    'Track any recurring symptoms, sleep changes, hydration, or recovery issues during the week.'
  ];

  if (hasMetricFlag(['BP Systolic', 'BP Diastolic', 'Hypertension Risk'])) {
    weekItems.push('Monitor blood pressure with a validated cuff if pressure-related metrics remain flagged.');
  }

  if (hasMetricFlag(['Hba1c', 'Hba1c Risk'])) {
    weekItems.push('Monitor meals, activity, and glucose-related habits if HbA1c markers were flagged.');
  }

  if (!weekItems.some((item) => item.includes('Monitor blood pressure')) && !weekItems.some((item) => item.includes('glucose-related'))) {
    weekItems.push('Keep the same healthy routine so future scans can be compared against a stable baseline.');
  }

  const medicalItems: string[] = [];

  if (hasMetricFlag(['BP Systolic', 'BP Diastolic', 'Hypertension Risk'])) {
    medicalItems.push('Consult a doctor to confirm abnormal blood pressure or hypertension risk with standard measurements.');
  }

  if (hasMetricFlag(['Hba1c', 'Hba1c Risk'])) {
    medicalItems.push('Use a lab-based HbA1c or glucose test if HbA1c-related markers remain outside range.');
  }

  if (hasMetricFlag(['Oxygen Saturation (SPO2)'])) {
    medicalItems.push('Confirm low SpO2 with a clinical-grade pulse oximeter or clinician review, especially if symptoms are present.');
  }

  if (hasMetricFlag(['Haemoglobin'])) {
    medicalItems.push('Discuss abnormal hemoglobin findings with a clinician and consider lab confirmation.');
  }

  if (!medicalItems.length) {
    medicalItems.push('Consult a doctor or arrange lab testing if abnormal readings repeat or symptoms develop.');
  }

  return [
    { title: 'Today', tone: 'info', items: todayItems },
    { title: 'This Week', tone: 'warning', items: weekItems },
    { title: 'Medical Confirmation', tone: 'danger', items: medicalItems }
  ];
};

const getSeriesStats = (values: number[]) => {
  const cleanValues = values.filter((value) => Number.isFinite(value));
  if (!cleanValues.length) {
    return null;
  }

  const min = Math.min(...cleanValues);
  const max = Math.max(...cleanValues);
  const latest = cleanValues[cleanValues.length - 1];
  const average = cleanValues.reduce((sum, current) => sum + current, 0) / cleanValues.length;

  return { min, max, latest, average };
};

const formatSkinParameterName = (key: string): { label: string; icon: string } => {
  const normalizedKey = normaliseTextKey(key);
  const mappedValue = skinParameterMap[normalizedKey];
  if (mappedValue) {
    return mappedValue;
  }

  return {
    label: key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase()),
    icon: '✨'
  };
};

const getSkinParameterTone = (score: number, maximumScore: number): StatusTone => {
  const ratio = maximumScore > 0 ? score / maximumScore : 0;

  if (ratio >= 0.8) {
    return 'skin';
  }

  if (ratio >= 0.6) {
    return 'warning';
  }

  return 'danger';
};

const preparePdfBadgeText = (rootElement: HTMLElement) => {
  const simplifyBadge = (
    selector: string,
    textSelector: string,
    options: { color: string; fontSize: string; letterSpacing?: string }
  ) => {
    rootElement.querySelectorAll(selector).forEach((badgeElement) => {
      const badge = badgeElement as HTMLElement;
      const textNode = badge.querySelector(textSelector) as HTMLElement | null;
      const exportText = (textNode?.textContent || badge.textContent || '').trim();

      if (!exportText) {
        return;
      }

      badge.innerHTML = '';
      badge.style.display = 'inline-block';
      badge.style.lineHeight = '1';
      badge.style.whiteSpace = 'nowrap';
      badge.style.gap = '0';

      const label = document.createElement('span');
      label.textContent = exportText.toUpperCase();
      label.style.display = 'inline-block';
      label.style.color = options.color;
      label.style.fontSize = options.fontSize;
      label.style.fontWeight = '700';
      label.style.lineHeight = '1';
      label.style.letterSpacing = options.letterSpacing || '0.04em';
      label.style.whiteSpace = 'nowrap';

      badge.appendChild(label);
    });
  };

  simplifyBadge('.section-kicker', '.section-kicker-text', {
    color: '#2b5b78',
    fontSize: '12px',
  });

  simplifyBadge('.skin-kicker', '.section-kicker-text', {
    color: '#68478f',
    fontSize: '12px',
  });

  simplifyBadge('.brand-pill', '.brand-pill-text', {
    color: '#ffffff',
    fontSize: '13px',
    letterSpacing: '0.05em',
  });
};

const preparePdfPageLayout = (rootElement: HTMLElement) => {
  rootElement.querySelectorAll('.report-panel').forEach((panelElement) => {
    const panel = panelElement as HTMLElement;
    const shouldAvoidBreak = panel.classList.contains('pdf-avoid-break');

    panel.style.overflow = 'visible';
    panel.style.breakInside = shouldAvoidBreak ? 'avoid' : 'auto';
    panel.style.pageBreakInside = shouldAvoidBreak ? 'avoid' : 'auto';
  });

  rootElement.querySelectorAll(
    '.metric-grid-col, .skin-grid-col, .recommendation-grid-col, .action-grid-col, .trend-grid-col, .insight-grid-col'
  ).forEach((columnElement) => {
    const column = columnElement as HTMLElement;
    column.style.breakInside = 'avoid';
    column.style.pageBreakInside = 'avoid';
  });

  rootElement.querySelectorAll('.metric-category-header, .skin-header-block').forEach((headerElement) => {
    const header = headerElement as HTMLElement;
    header.style.breakAfter = 'avoid';
    header.style.pageBreakAfter = 'avoid';
  });
};

const CircularScore: React.FC<CircularScoreProps> = ({ score, label, tone, size = 176, suffix = '/100', secondaryText }) => {
  const palette = tonePalette[tone];
  const clampedScore = Math.max(0, Math.min(100, score));
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (clampedScore / 100) * circumference;

  return (
    <div className="circular-score">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="circular-score-svg" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={palette.track}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={palette.base}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="circular-score-content">
        <div className="circular-score-value-group">
          <span className="circular-score-value" style={{ color: palette.base }}>{formatValue(clampedScore)}</span>
          <span className="circular-score-suffix">{suffix}</span>
        </div>
        <span className="circular-score-label">{label}</span>
        {secondaryText && <span className="circular-score-secondary">{secondaryText}</span>}
      </div>
    </div>
  );
};

const Sparkline: React.FC<SparklineProps> = ({ values, tone = 'info' }) => {
  const palette = tonePalette[tone];
  const cleanValues = values.filter((value) => Number.isFinite(value));

  if (cleanValues.length < 2) {
    return (
      <svg viewBox="0 0 240 72" className="sparkline-svg" role="img" aria-label="Trend unavailable">
        <line x1="16" y1="36" x2="224" y2="36" stroke={palette.track} strokeWidth="4" strokeDasharray="6 6" />
      </svg>
    );
  }

  const minValue = Math.min(...cleanValues);
  const maxValue = Math.max(...cleanValues);
  const range = maxValue - minValue || 1;

  const points = cleanValues.map((value, index) => {
    const x = 16 + (index / (cleanValues.length - 1)) * 208;
    const y = 60 - ((value - minValue) / range) * 40;
    return `${x},${y}`;
  });

  const areaPoints = [`16,60`, ...points, `224,60`].join(' ');

  return (
    <svg viewBox="0 0 240 72" className="sparkline-svg" role="img" aria-label="Trend line">
      <path d={`M ${points.join(' L ')}`} fill="none" stroke={palette.base} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      <polygon points={areaPoints} fill={palette.soft} />
    </svg>
  );
};

const RangeBar: React.FC<RangeBarProps> = ({ metricName, value }) => {
  const ranges = metricRanges[metricName];
  if (!ranges || !ranges.length) {
    return <div className="range-bar-empty">Reference range not available.</div>;
  }

  const min = ranges[0].min;
  const max = ranges[ranges.length - 1].max;
  const safeSpan = max - min || 1;
  const clampedValue = Math.max(min, Math.min(max, value));
  const position = ((clampedValue - min) / safeSpan) * 100;
  const currentLabelPosition = Math.max(12, Math.min(88, position));
  const decimals = getMetricDecimals(metricName, value);
  const rangeLayout = ranges.map((range) => {
    const left = ((range.min - min) / safeSpan) * 100;
    const width = ((range.max - range.min) / safeSpan) * 100;

    return {
      range,
      left,
      width,
    };
  });

  return (
    <div className="range-bar-wrapper">
      <div className="range-bar-track">
        {rangeLayout.map(({ range, left, width }) => {
          const tone = tonePalette[getRangeTone(range)];

          return (
            <span
              key={`${metricName}-${range.label}`}
              className="range-bar-segment"
              style={{
                left: `${left}%`,
                width: `${width}%`,
                backgroundColor: tone.track
              }}
            />
          );
        })}
        <span className="range-bar-marker" style={{ left: `${position}%` }} />
      </div>
      <div className="range-bar-legend">
        {rangeLayout.map(({ range, left, width }) => (
          <span
            key={`${metricName}-${range.label}-legend`}
            className="range-bar-legend-item"
            style={{ left: `${left}%`, width: `${width}%` }}
          >
            {range.label}
          </span>
        ))}
      </div>
      <div className="range-bar-values">
        <span className="range-bar-value range-bar-value-start">{formatValue(min, min < 1 ? 1 : 0)}</span>
        <span className="range-bar-current" style={{ left: `${currentLabelPosition}%` }}>
          Current {formatValue(value, decimals)}
        </span>
        <span className="range-bar-value range-bar-value-end">{formatValue(max, max < 1 ? 1 : 0)}</span>
      </div>
    </div>
  );
};

const ScanResults: React.FC<ScanResultsProps> = ({ data, userData, onNewScan, onDownload }) => {
  const printRef = useRef<HTMLDivElement>(null);

  const allMetrics = processMetrics(data);
  const orderedMetrics = Object.values(allMetrics).sort((metricA, metricB) => {
    const orderA = metricDisplayOrderLookup.get(metricA.name);
    const orderB = metricDisplayOrderLookup.get(metricB.name);

    if (orderA === undefined && orderB === undefined) {
      return metricA.name.localeCompare(metricB.name);
    }

    if (orderA === undefined) {
      return 1;
    }

    if (orderB === undefined) {
      return -1;
    }

    return orderA - orderB;
  });

  const needsAttention = orderedMetrics.filter((metric) => metric.badge !== 'NORMAL');
  const inRange = orderedMetrics.filter((metric) => metric.badge === 'NORMAL');

  const groupedMetrics = categoryOrder
    .map((category) => ({
      category,
      metrics: orderedMetrics.filter((metric) => metric.category === category)
    }))
    .filter((group) => group.metrics.length > 0);

  const userAge = userData?.age ?? data.metadata?.physiological_scores?.age ?? data.age;
  const userGender = userData?.gender ?? data.metadata?.physiological_scores?.gender ?? data.gender;
  const userName = userData?.name ?? data.user_name ?? data.name ?? 'Guest';
  const userHeight = userData?.height ?? data.metadata?.physiological_scores?.height;
  const userWeight = userData?.weight ?? data.metadata?.physiological_scores?.weight;
  const scanDateValue = data.scan_completion_time ?? data.timestamp ?? data.scan_date;
  const scanId = data.scan_id || data.request_id || 'Not available';
  const wellnessScore = parseNumericValue(data.wellness_score) ?? allMetrics['Health Score']?.value ?? 0;
  const wellnessSummary = getWellnessSummary(wellnessScore, needsAttention.length, orderedMetrics.length);
  const topInsights = buildTopInsights(orderedMetrics);
  const skinReport = data.skinScan?.skin_report;
  const skinScore = parseNumericValue(skinReport?.skin_score);
  const recommendedActions = buildRecommendedActions(orderedMetrics, skinReport);

  const trendCards = [
    {
      title: 'Heart Rate Trend',
      values: data.metadata?.heartrate_time || [],
      unit: 'bpm',
      tone: 'danger' as StatusTone,
      subtitle: 'Beat-to-beat heart rate pattern captured during the scan.'
    },
    {
      title: 'Respiration Rate Trend',
      values: data.metadata?.resp_rate_time || [],
      unit: 'brpm',
      tone: 'info' as StatusTone,
      subtitle: 'Breathing rhythm snapshot across the scan window.'
    }
  ];

  const skinParameters = skinReport?.parameters
    ? Object.entries(skinReport.parameters)
        .map(([key, parameter]) => {
          const formattedParameter = formatSkinParameterName(key);
          const scoreRatio = parameter.maximum_score > 0 ? parameter.score / parameter.maximum_score : 0;

          return {
            key,
            label: formattedParameter.label,
            icon: formattedParameter.icon,
            scoreRatio,
            parameter
          };
        })
        .sort((parameterA, parameterB) => parameterA.scoreRatio - parameterB.scoreRatio)
    : [];

  const focusAreas = skinParameters.slice(0, 3);

  const routineCards: Array<{ title: SkinRoutineTitle; items: string[]; icon: string }> = (
    ['Morning Routine', 'Evening Routine', 'Weekly Treatments', 'Lifestyle Tips'] as SkinRoutineTitle[]
  ).map((title) => ({
    title,
    items: skinReport?.summary_and_recommendations?.[title] || defaultSkinRecommendations[title],
    icon: skinRoutineIcons[title]
  }));

  const handleDownloadPDF = async () => {
    const element = printRef.current;
    if (!element) {
      return;
    }

    const userNameForFile = (userName || 'Patient').replace(/[^a-z0-9]+/gi, '_');
    const parsedScanDate = parseDateValue(scanDateValue) || new Date();
    const dateStr = parsedScanDate.toISOString().split('T')[0];
    const filename = `AI_Face_Scan_Wellness_Report_${userNameForFile}_${dateStr}.pdf`;
    const clonedElement = element.cloneNode(true) as HTMLElement;

    clonedElement.style.backgroundColor = '#ffffff';
    clonedElement.querySelectorAll('.hide-on-print, .btn').forEach((node) => {
      (node as HTMLElement).style.display = 'none';
    });
    preparePdfBadgeText(clonedElement);
    preparePdfPageLayout(clonedElement);

    const options = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      filename,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: {
        scale: 2.5,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        letterRendering: true,
        scrollY: 0,
        scrollX: 0
      },
      jsPDF: {
        unit: 'mm' as const,
        format: 'a4' as const,
        orientation: 'portrait' as const,
        compress: true
      },
      pagebreak: {
        mode: ['css', 'legacy'] as const,
        before: '.pdf-page-break',
        avoid: ['.pdf-avoid-break', '.metric-card', '.skin-header-block']
      }
    };

    const button = printRef.current.querySelector('.download-pdf-btn') as HTMLButtonElement | null;
    const originalButtonMarkup = button?.innerHTML;

    try {
      if (button) {
        button.disabled = true;
        button.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Generating PDF...';
      }

      await html2pdf().set(options).from(clonedElement).save();
      onDownload?.();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      if (button) {
        button.disabled = false;
        button.innerHTML = originalButtonMarkup || 'Download Report as PDF';
      }
    }
  };

  const headerDetails = [
    { label: 'Scan ID', value: scanId },
    { label: 'Name', value: userName || 'Not available' },
    { label: 'Age', value: parseNumericValue(userAge) !== null ? `${formatValue(userAge)} years` : 'Not available' },
    { label: 'Gender', value: userGender ? formatGender(userGender) : 'Not available' },
    { label: 'Height', value: parseNumericValue(userHeight) !== null ? `${formatValue(userHeight)} cm` : 'Not available' },
    { label: 'Weight', value: parseNumericValue(userWeight) !== null ? `${formatValue(userWeight)} kg` : 'Not available' }
  ];

  const summaryChips = [
    {
      label: 'Needs Attention',
      value: String(needsAttention.length),
      tone: 'warning' as StatusTone
    },
    {
      label: 'In Range',
      value: String(inRange.length),
      tone: 'success' as StatusTone
    },
    {
      label: 'Skin Score',
      value: skinScore !== null ? `${formatValue(skinScore)}/100` : 'Not scanned',
      tone: 'skin' as StatusTone
    }
  ];

  return (
    <>
      <div ref={printRef} className="scan-report">
        <div className="scan-shell">
          <Card className="report-panel report-section border-0 pdf-avoid-break">
            <Card.Body className="p-0">
              <div className="report-gradient-header">
                <Row className="g-4 align-items-end">
                  <Col lg={7}>
                    <div className="brand-pill">
                      <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-3 text-white' />
                      <span className="brand-pill-text">Futurisk Wellness</span>
                    </div>
                    <h1 className="report-title">AI Face Scan Wellness Report</h1>
                    <p className="report-subtitle">
                      Premium screening summary of your latest face scan and skin analysis, designed to make key trends, focus areas, and next steps easy to review.
                    </p>
                    <span className="report-disclaimer">AI-assisted screening report. Not a medical diagnosis.</span>
                  </Col>
                  <Col lg={5}>
                    <div className="header-detail-grid">
                      {headerDetails.map((detail) => (
                        <div className="header-detail-card" key={detail.label}>
                          <span className="header-detail-label">{detail.label}</span>
                          <span className="header-detail-value">{detail.value}</span>
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>

          <Card className="report-panel report-section border-0 pdf-avoid-break">
            <Card.Body className="p-4 p-lg-5">
              <Row className="g-4 align-items-center">
                <Col lg={4} className="text-center text-lg-start">
                  <CircularScore
                    score={wellnessScore}
                    label="Wellness Score"
                    tone={getScoreTone(wellnessScore)}
                    secondaryText={wellnessSummary.label}
                  />
                </Col>
                <Col lg={8}>
                  <span className="section-kicker"><span className="section-kicker-text">Executive Summary</span></span>
                  <h2 className="section-heading">Overall wellness snapshot</h2>
                  <div className="summary-chip-row">
                    {summaryChips.map((chip) => (
                      <span className={`summary-chip tone-${chip.tone}`} key={chip.label}>
                        <small>{chip.label}</small>
                        {chip.value}
                      </span>
                    ))}
                  </div>
                  <p className="section-copy">{wellnessSummary.text}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="report-panel report-section border-0 pdf-avoid-break">
            <Card.Body className="p-4 p-lg-5">
              <span className="section-kicker"><span className="section-kicker-text">Top Insights</span></span>
              <h2 className="section-heading">Key observations from this scan</h2>
              <Row className="g-4">
                {topInsights.map((insight) => (
                  <Col md={6} xl={4} className="insight-grid-col" key={insight.title}>
                    <div className={`insight-card tone-${insight.tone}`}>
                      <div className="insight-title">{insight.title}</div>
                      <p className="insight-copy">{insight.text}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>

          <Card className="report-panel report-section border-0 pdf-avoid-break">
            <Card.Body className="p-4 p-lg-5">
              <span className="section-kicker"><span className="section-kicker-text">Scan Trend Snapshot</span></span>
              <h2 className="section-heading">Heart rate and respiration pattern</h2>
              <Row className="g-4 trend-grid">
                {trendCards.map((trend) => {
                  const stats = getSeriesStats(trend.values);

                  return (
                    <Col md={6} className="trend-grid-col" key={trend.title}>
                      <div className="trend-card">
                        <span className="trend-eyebrow">Trend</span>
                        <div className="trend-title">{trend.title}</div>
                        <p className="trend-copy">{trend.subtitle}</p>
                        <Sparkline values={trend.values} tone={trend.tone} />
                        {stats ? (
                          <div className="trend-stat-row">
                            <span className={`trend-stat tone-${trend.tone}`}><small>Latest</small>{formatValue(stats.latest, getMetricDecimals(trend.title, stats.latest))} {trend.unit}</span>
                            <span className="trend-stat tone-neutral"><small>Average</small>{formatValue(stats.average, 1)} {trend.unit}</span>
                            <span className="trend-stat tone-neutral"><small>Range</small>{formatValue(stats.min, 0)}-{formatValue(stats.max, 0)} {trend.unit}</span>
                          </div>
                        ) : (
                          <div className="trend-stat-row">
                            <span className="trend-stat tone-neutral">Trend data was not captured for this metric in the current scan.</span>
                          </div>
                        )}
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>

          <Card className="report-panel report-section border-0 pdf-page-break">
            <Card.Body className="p-4 p-lg-5">
              <span className="section-kicker"><span className="section-kicker-text">Metrics</span></span>
              <h2 className="section-heading">Detailed health metrics</h2>
              {groupedMetrics.map((group) => (
                <div className="metric-category-block" key={group.category}>
                  <div className="metric-category-header">
                    <div>
                      <div className="metric-category-title">{group.category}</div>
                      <p className="metric-category-copy">{categoryDescriptions[group.category]}</p>
                    </div>
                    <span className="category-count tone-neutral">{group.metrics.length} metrics</span>
                  </div>
                  <Row className="g-4 metric-grid">
                    {group.metrics.map((metric) => {
                      const tone = getMetricTone(metric);
                      const palette = tonePalette[tone];
                      const decimals = getMetricDecimals(metric.name, metric.value);

                      return (
                        <Col xs={12} className="metric-grid-col" key={metric.name}>
                          <div className="metric-card pdf-avoid-break">
                            <div className="metric-card-grid">
                              <div className="metric-overview">
                                <div className="metric-header-row">
                                  <div className="metric-icon" style={{ background: palette.soft, border: `1px solid ${palette.border}` }}>
                                    {metric.icon}
                                  </div>
                                  <div>
                                    <span className="metric-eyebrow">{metric.category}</span>
                                    <div className="metric-card-title">{metric.name}</div>
                                  </div>
                                </div>
                                <div className="metric-value-row">
                                  <span className="metric-value" style={{ color: palette.base }}>
                                    {formatValue(metric.value, decimals)}
                                  </span>
                                  {metric.unit && <span className="metric-unit">{metric.unit}</span>}
                                </div>
                                <span className={`metric-pill tone-${tone}`}>{metric.detailedLabel}</span>
                              </div>

                              <div className="metric-range-panel">
                                <span className="metric-block-title">Reference range</span>
                                <RangeBar metricName={metric.name} value={metric.value} />
                                <p className="metric-range-copy mt-3">{metric.rangeText}</p>
                              </div>

                              <div className="metric-advice-panel">
                                <span className="metric-block-title">What it means</span>
                                <p className="metric-advice-copy">{metric.meaning}</p>
                                <span className="metric-block-title">Suggested action</span>
                                <p className="metric-advice-copy">{metric.suggestion}</p>
                              </div>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              ))}
            </Card.Body>
          </Card>

          {skinReport && (
            <Card className="report-panel report-section border-0 skin-section pdf-page-break">
              <Card.Body className="p-4 p-lg-5">
                <div className="skin-header-block pdf-avoid-break">
                  <span className="section-kicker skin-kicker"><span className="section-kicker-text">Skin Analysis</span></span>
                  <h2 className="section-heading">Skin score, focus areas, and routine guidance</h2>
                </div>

                <Row className="g-4 align-items-center pdf-avoid-break">
                  <Col lg={4} className="text-center text-lg-start">
                    <CircularScore
                      score={skinScore || 0}
                      label="Skin Score"
                      tone={getScoreTone(skinScore || 0, 'skin')}
                      secondaryText={skinReport.skin_grade.toUpperCase()}
                    />
                  </Col>
                  <Col lg={8}>
                    <div className="skin-summary-grid">
                      <div className="skin-summary-card">
                        <span className="skin-score-eyebrow">Estimated Skin Age</span>
                        <div className="skin-card-title">{formatValue(skinReport.estimated_skin_age)} years</div>
                        <p className="skin-card-copy">A lower estimated skin age generally reflects a stronger skin barrier, hydration, and tone profile in this scan.</p>
                      </div>
                      <div className="skin-summary-card">
                        <span className="skin-score-eyebrow">Skin Grade</span>
                        <div className="skin-card-title">{skinReport.skin_grade.toUpperCase()}</div>
                        <p className="skin-card-copy">Use this as a trend marker rather than a diagnosis, and compare again after following the routine suggestions consistently.</p>
                      </div>
                    </div>
                  </Col>
                </Row>

                {focusAreas.length > 0 && (
                  <div className="skin-focus-panel pdf-avoid-break">
                    <span className="section-kicker skin-kicker"><span className="section-kicker-text">Main Skin Focus Areas</span></span>
                    <div className="skin-focus-chip-row">
                      {focusAreas.map((focusArea) => (
                        <span className="focus-chip tone-skin" key={focusArea.key}>
                          <small>{focusArea.label}</small>
                          {formatValue(focusArea.parameter.score)} / {formatValue(focusArea.parameter.maximum_score)}
                        </span>
                      ))}
                    </div>
                    <p className="section-copy">
                      {focusAreas.map((focusArea) => focusArea.label).join(', ')} are the lowest scoring skin concerns in this scan. Prioritize gentle hydration, barrier support, and consistent routine care before the next check.
                    </p>
                  </div>
                )}

                <Row className="g-4 skin-parameter-grid">
                  {skinParameters.map((skinParameter) => {
                    const tone = getSkinParameterTone(skinParameter.parameter.score, skinParameter.parameter.maximum_score);
                    const palette = tonePalette[tone];
                    const progressWidth = skinParameter.parameter.maximum_score > 0
                      ? `${Math.max(8, (skinParameter.parameter.score / skinParameter.parameter.maximum_score) * 100)}%`
                      : '0%';

                    return (
                      <Col md={6} xl={4} className="skin-grid-col" key={skinParameter.key}>
                        <div className="skin-parameter-card pdf-avoid-break">
                          <div className="skin-card-header">
                            <div className="skin-icon" style={{ background: palette.soft, border: `1px solid ${palette.border}` }}>{skinParameter.icon}</div>
                            <div>
                              <span className="metric-eyebrow">Skin parameter</span>
                              <div className="skin-card-title">{skinParameter.label}</div>
                            </div>
                          </div>
                          <div className="skin-card-score mt-3">
                            {formatValue(skinParameter.parameter.score)}
                            <span>/ {formatValue(skinParameter.parameter.maximum_score)}</span>
                          </div>
                          <span className={`metric-pill tone-${tone}`}>{skinParameter.parameter.label}</span>
                          <div className="skin-card-progress">
                            <div className="skin-card-progress-fill" style={{ width: progressWidth, background: palette.base }} />
                          </div>
                          <p className="skin-card-copy">{skinParameter.parameter.comment}</p>
                        </div>
                      </Col>
                    );
                  })}
                </Row>

                <div className="mt-4 pdf-avoid-break">
                  <span className="section-kicker skin-kicker"><span className="section-kicker-text">Skincare Guidance</span></span>
                  <h3 className="section-heading h4">Daily and weekly routine</h3>
                  <Row className="g-4 recommendation-grid">
                    {routineCards.map((routineCard) => (
                      <Col md={6} className="recommendation-grid-col" key={routineCard.title}>
                        <div className="recommendation-card pdf-avoid-break">
                          <div className="skin-card-header">
                            <div className="recommendation-icon" style={{ background: tonePalette.skin.soft, border: `1px solid ${tonePalette.skin.border}` }}>
                              {routineCard.icon}
                            </div>
                            <div>
                              <span className="recommendation-eyebrow">Routine</span>
                              <div className="recommendation-title">{routineCard.title}</div>
                            </div>
                          </div>
                          <ul className="recommendation-list mt-3">
                            {routineCard.items.map((step, index) => (
                              <li key={`${routineCard.title}-${index}`}>{step}</li>
                            ))}
                          </ul>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Card.Body>
            </Card>
          )}

          <Card className="report-panel report-section border-0 pdf-page-break">
            <Card.Body className="p-4 p-lg-5">
              <span className="section-kicker"><span className="section-kicker-text">Recommended Next Steps</span></span>
              <h2 className="section-heading">Safe follow-up plan after this scan</h2>
              <Row className="g-4 action-grid">
                {recommendedActions.map((actionCard) => (
                  <Col md={4} className="action-grid-col" key={actionCard.title}>
                    <div className={`action-plan-card tone-${actionCard.tone} pdf-avoid-break`}>
                      <span className="action-plan-eyebrow">Action Plan</span>
                      <div className="action-plan-title">{actionCard.title}</div>
                      <ul className="action-plan-list mt-3">
                        {actionCard.items.map((item, index) => (
                          <li key={`${actionCard.title}-${index}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>

          <Alert className="report-footer-note report-section border-0" variant="light">
            <Alert.Heading className="h6 mb-2">Medical disclaimer</Alert.Heading>
            <p className="mb-0 small">
              This PDF is intended for wellness screening and trend review only. It should not replace clinical assessment, laboratory testing, or medical advice. If repeated scans remain outside range or symptoms are present, consult a qualified healthcare professional.
            </p>
          </Alert>

          <Row className="g-3 hide-on-print report-actions">
            {onNewScan && (
              <Col xs={12} md={6}>
                <Button variant="primary" className="w-100" onClick={onNewScan}>
                  <KTSVG path='/media/icons/duotune/arrows/arr021.svg' className='svg-icon-3 me-2' />
                  New Scan
                </Button>
              </Col>
            )}
            <Col xs={12} md={onNewScan ? 6 : 12}>
              <Button variant="outline-primary" className="w-100 download-pdf-btn" onClick={handleDownloadPDF}>
                <KTSVG path='/media/icons/duotune/arrows/arr091.svg' className='svg-icon-3 me-2' />
                Download Report as PDF
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default ScanResults;

